import React, { useState, useRef, useCallback, useMemo } from 'react';
import classNames from 'classnames';
import moment from 'moment';
import { Tooltip } from 'antd';
import Loader from '@components/Loader';
import { AssetItemDto } from '@api/Api';
import {
  getMediaDuration,
  getVideoScrubInfo,
  isAudioVideo
} from '@helpers/avInfo';
import { formatDuration } from '@helpers/formatDuration';
import { assetPreviewThumbnails } from '@helpers/assetPreviewThumbnails';
import Status from '@components/Approval/Status';
import { nonNull } from '@helpers/non-null';
import { BrokenLinkIcon } from '@icons';
import './AssetCard.less';

type AssetCardProps = {
  asset: AssetItemDto;
  isSelected: boolean;
  onToggleSelect: (asset: AssetItemDto, isSelected: boolean) => void;
};

export default function AssetCard({
  asset,
  isSelected,
  onToggleSelect
}: AssetCardProps) {
  const scrubRef = useRef<any>();
  const scrubLineRef = useRef<any>();
  const [scrubsVisible, setScrubsVisible] = useState<boolean>(false);

  const version = nonNull(asset.asset?.versions[0]);
  const { scrubsUrl, frameWidth, frameHeight, framesCount } = getVideoScrubInfo(
    version.metadata
  );
  const duration = isAudioVideo(version.metadata)
    ? getMediaDuration(version.metadata)
    : null;

  const onMouseEnter = useCallback(() => {
    if (version.metadata.type !== 'video') return;
    setScrubsVisible(true);
    scrubLineRef.current.style.display = 'block';
  }, [setScrubsVisible, version]);

  const onMouseLeave = useCallback(() => {
    if (version.metadata.type !== 'video') return;
    setScrubsVisible(false);
    scrubLineRef.current.style.display = 'none';
  }, [setScrubsVisible, version]);

  const onMouseMove = useCallback(
    async (e: any) => {
      const canvasEl = scrubRef.current;
      if (!canvasEl || !scrubsUrl) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const stepWidth = rect.width / framesCount;
      const frameIndex = Math.floor((e.clientX - rect.left) / stepWidth);
      const img = new Image();
      canvasEl.frameIndex = frameIndex;
      img.src = scrubsUrl;
      img.onload = () => {
        const canvasEl = scrubRef.current;
        if (!canvasEl || canvasEl.frameIndex !== frameIndex) return;

        if (scrubLineRef?.current) {
          scrubLineRef.current.style.left = `${Math.floor(
            e.clientX - rect.left
          )}px`;
        }
        const ctx = scrubRef.current.getContext('2d');
        ctx.drawImage(
          img,
          0,
          frameIndex * frameHeight,
          frameWidth,
          frameHeight,
          0,
          0,
          frameWidth,
          frameHeight
        );
      };
    },
    [scrubsUrl, frameWidth, frameHeight, scrubLineRef, framesCount, scrubRef]
  );

  const preparePreview = useMemo(() => {
    const res = assetPreviewThumbnails({
      url: version.metadata.preview?.url || '',
      status: version.metadata.preview?.status || '',
      type: version.metadata.type || '',
      format: 'img'
    });

    if (res === 'pending') return 'pending';
    if (typeof res === 'string')
      return <img draggable={false} src={res || ''} alt="" />;
    return res;
  }, [version]);

  return (
    <div
      className={classNames('asset-card', {
        'b-selected': isSelected
      })}
    >
      <div className="asset-card_preview">
        <Tooltip
          placement="topLeft"
          overlayClassName="asset-card_tooltip"
          title={
            isSelected
              ? 'Unselect to keep this media in the active approval routing'
              : 'Select to remove this media from the active approval routing'
          }
        >
          <div
            className="asset-card_broken-link"
            onClick={() => onToggleSelect(asset, isSelected)}
          >
            <BrokenLinkIcon />
          </div>
        </Tooltip>
        {preparePreview === 'pending' && (
          <Loader
            classNameWrapper="asset-card_preview-loader"
            spinSize={40}
            isSpinning={true}
          />
        )}
        <div
          className="asset-card_image"
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          onMouseMove={onMouseMove}
        >
          {preparePreview !== 'pending' && preparePreview}
          {scrubsVisible && (
            <canvas
              ref={scrubRef}
              width={frameWidth}
              height={frameHeight}
              className="asset-card_image-canvas"
            />
          )}
          <div ref={scrubLineRef} className="asset-card_image-line" />
        </div>
        <div className="asset-card_preview-info">
          <div className="asset-card_preview-info-left">
            {!!asset.asset?.approvalWorkflow && (
              <Status
                status={asset.asset.approvalWorkflow.status}
                approvalWorkflowStatus={asset.asset.approvalWorkflow.status}
                approvalWorkflowIsPaused={asset.asset.approvalWorkflow.isPaused}
                deadline={asset.asset.approvalWorkflow.deadline}
              />
            )}
          </div>
          {!!duration && (
            <div className="asset-card_preview-info-duration">
              {formatDuration(duration)}
            </div>
          )}
        </div>
      </div>
      <div className="asset-card_content">
        <Tooltip
          placement="topLeft"
          overlayClassName="asset-card_tooltip"
          title={`${version.name}.${version.extension}`}
        >
          <div className="asset-card_name">{version.name}</div>
        </Tooltip>
        <div className="asset-card_date">
          Edited {moment(asset.modifiedAt).fromNow()}
        </div>
      </div>
    </div>
  );
}
